import { Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ChangeDetectorRef } from '@angular/core';

import { FolderItem } from 'src/app/api/services/folder';
import { PresentationSelectorModalComponent } from '../presentation-selector-modal/presentation-selector-modal.component';
import { FolderService } from 'src/app/shared/services/folder.service';
import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { FolderBrowserService } from 'src/app/components/folders/services/folder-browser.service';
import { ScrollingListFactoryService } from 'src/app/components/scrolling-list/services/scrolling-list-factory.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { ENTITY_TYPES } from 'src/app/auth/types/entity-type';

type PresentationSelectorRow = {
  id?: string;
  name?: string;
  presentationType?: string;
  folderId?: string;
  selected?: boolean;
  revisionStatusName: string;
  isFolder?: false;
};

type PresentationSelectorItem = FolderItem | PresentationSelectorRow;

@Component({
  selector: 'presentation-multi-selector',
  templateUrl: './presentation-multi-selector.component.html',
  styleUrls: ['./presentation-multi-selector.component.scss'],
  standalone: false,
  providers: [FolderBrowserService],
})
export class PresentationMultiSelectorComponent extends PresentationSelectorModalComponent {
  private permissionService = inject(PermissionService);

  constructor(
    dialogRef: MatDialogRef<PresentationMultiSelectorComponent>,
    changeDetectorRef: ChangeDetectorRef,
    scrollingListFactory: ScrollingListFactoryService,
    presentationApiService: PresentationApiService,
    private folderService: FolderService,
    public folderBrowser: FolderBrowserService,
  ) {
    super(
      dialogRef,
      changeDetectorRef,
      scrollingListFactory,
      presentationApiService,
    );

    if (this.foldersSupported) {
      this.folderBrowser.init('Presentation');
    }
  }

  get foldersSupported(): boolean {
    return this.permissionService.areFoldersSupported(
      ENTITY_TYPES.PRESENTATION,
    );
  }

  areAllPresentationsSelected(): boolean {
    const presentations = this.presentations.items.list;
    return (
      presentations.length > 0 &&
      presentations.every((presentation) => presentation.selected)
    );
  }

  selectAll(checked: boolean) {
    const allSelected = this.areAllPresentationsSelected();
    this.presentations.items.list.forEach((presentation) => {
      if (allSelected) {
        presentation.selected = false;
      } else {
        presentation.selected = true;
      }
    });
  }

  add() {
    this.dialogRef.close(this.presentations.getSelected());
  }

  onFolderClicked(folderId?: string) {
    this.folderBrowser.open(folderId);
    this.search.folderId = folderId;
    this.presentations.doSearch();
  }

  get items(): PresentationSelectorItem[] {
    const presentations: PresentationSelectorRow[] =
      this.presentations.items?.list || [];

    if (!this.foldersSupported) {
      return presentations;
    }

    const folders = this.folderBrowser.folders();
    const items = presentations.filter((item) =>
      this.folderService.showFolderItem(
        this.search.folderId || FoldersApiService.ROOT_FOLDER_ID,
        item.folderId,
      ),
    );

    return [...folders, ...items];
  }

  isFolderItem(item: PresentationSelectorItem): item is FolderItem {
    return item.isFolder === true;
  }

  selectPresentation(item: PresentationSelectorItem) {
    if (this.isFolderItem(item)) {
      return;
    }

    this.presentations.select(item);
  }

  get isLoading() {
    return (
      this.presentations.loadingItems ||
      (this.foldersSupported && this.folderBrowser.loading())
    );
  }
}
